import React, { useRef, useState } from 'react';
import { ImagePlus, RefreshCw } from 'lucide-react';
import { uploadImage } from '../../lib/upload';
import { UploadOverlay } from './CoverUpload';

export const fieldCls =
  'w-full bg-[#0f0f0f] border border-white/10 rounded-lg px-4 py-3 text-[#f4efe6] placeholder:text-[#5a5a5a] focus:outline-none focus:border-[#00b8ff]';

export default function ImageBlockField({ block, onChange, testid }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    setProgress(0);
    try {
      const res = await uploadImage(file, setProgress);
      onChange({ ...block, src: res.url, width: res.width, height: res.height });
    } catch (err) {
      setError(err.message);
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative rounded-lg overflow-hidden border border-dashed border-white/15 bg-[#0f0f0f] min-h-[160px]">
        {block.src ? (
          <img src={block.src} alt={block.alt || 'Article photo'} className="w-full max-h-[360px] object-contain" data-testid={`${testid}-preview`} />
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute inset-0 flex flex-col items-center justify-center text-[#7a7a7a] text-sm gap-2 hover:text-[#00b8ff] transition-colors duration-200"
            data-testid={`${testid}-empty`}
          >
            <ImagePlus className="w-8 h-8" />Choose a photo
          </button>
        )}
        {progress !== null && <UploadOverlay progress={progress} testid={`${testid}-progress`} />}
      </div>
      {block.src && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={progress !== null}
          className="inline-flex items-center gap-2 text-sm text-[#cfc8bb] hover:text-[#00b8ff] transition-colors duration-200"
          data-testid={`${testid}-replace`}
        >
          <RefreshCw className="w-4 h-4" />Replace photo
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testid}-file`} />
      {error && <p className="text-sm text-red-400" data-testid={`${testid}-error`}>{error}</p>}
      <input
        value={block.alt || ''}
        onChange={(e) => onChange({ ...block, alt: e.target.value })}
        placeholder="Description for Google & screen readers (e.g. Crew screen printing tees at a festival)"
        className={fieldCls}
        data-testid={`${testid}-alt`}
      />
      <input
        value={block.caption || ''}
        onChange={(e) => onChange({ ...block, caption: e.target.value })}
        placeholder="Caption shown under the photo (optional)"
        className={fieldCls}
        data-testid={`${testid}-caption`}
      />
    </div>
  );
}
